import { useEffect, useState } from 'react';
import { Box, Button, FormControl, MenuItem, Select, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import { KeyValues } from "../models/Items/FilterOptions";
import { CSSDefaults } from "../models/GlobalConstants";
import ConfirmationModal from "./ConfirmationModal";
import SessionService from '../services/SessionService';
import { Permissions, PermissionsFriendlyNames } from '../models/Enum';
import { VIEW_PRODUCT_ASSIGNMENT } from '../pages/Products/EditItem/Constants';
import { GridValidRowModel, GridColDef } from '@mui/x-data-grid';
import GridForReadonlyData from './GridForReadonlyData';
import { useAppDispatch } from '../store';
import { initialProductAssignmentsListRequest, updateProductAssignmentsListRequest, updateProductAssignmentsViewSelectedFilterOptions, updateProductAssignmentsViewSelectedFilterValues } from '../reducers/productAssignmentReducer';

type UserAssignmentsProps = {
  selectedUserAssignments: UserAssignmentFieldValue[];
  setSelectedUserAssignments: (selectedUserAssignments: UserAssignmentFieldValue[]) => void;
  viewMode: string;
  assignmentRoleDropdownOptions: KeyValues[];
  userDropdownOptions: UserDropdownOptions[];
  classNames?: string;
  baseCode?: string;
  shortDesc?: string;
  editable?: boolean
  isCopyMode?: boolean;
};

export interface UserAssignmentFieldValue {
  [key: string]: string | number;
};

export interface UserDropdownOptions {
  id: number;
  name: string;
};

type UserDropdownValue = {
  index: number;
  field: string;
  value: number;
};

const UserAssignments = (props: UserAssignmentsProps) => {

  const {
    selectedUserAssignments,
    setSelectedUserAssignments,
    viewMode,
    assignmentRoleDropdownOptions,
    userDropdownOptions,
    classNames,
    baseCode,
    shortDesc,
    editable,
    isCopyMode
  } = props;

  const [isAddNewRowDisabled, setIsAddNewRowDisabled] = useState<boolean>(false);
  const [showDropdownChangeConfirmationModal, setShowDropdownChangeConfirmationModal] = useState(false);
  const [newDropdownValue, setNewDropdownValue] = useState<UserDropdownValue | null>(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState<number | null>(null);

  const sessionService = SessionService.getInstance();
  const dispatch = useAppDispatch();

  // This is used to disable add new row button if assignment role dropdown value is empty
  useEffect(() => {
    if (selectedUserAssignments) {
      const containsEmptyString = selectedUserAssignments.find(obj => obj.assignmentId === "");
      setIsAddNewRowDisabled(!!containsEmptyString);
    }
  }, [selectedUserAssignments]);

  // This function is used to add new row
  const handleAddNewRow = () => {
    setSelectedUserAssignments([...selectedUserAssignments, { userId: "", assignmentId: "" }]);
  };

  /**
   * This function is used to remove user assignment row
   * @param index - Index value of user assignment row array
   */
  const handleUserAssignmentRemove = (index: number) => {
    // Check if user assignment dropdown(s) value is not empty for displaying confirmation dialog
    const checkIsValueNotEmpty = selectedUserAssignments
      .filter((_, i) => i === index)
      .some((ua) => ua?.userId !== "" && ua?.assignmentId !== "");

    // Check if view mode is EditProduct and user assignment dropdown(s) contains selected value from dropdown options
    if (viewMode === "EditProduct" && checkIsValueNotEmpty) {
      setShowDeleteConfirmationModal(true);
      setDeleteRowIndex(index);
    }
    // else remove user assignment row with updating state
    else {
      const updateSelectedUserAssignments = selectedUserAssignments.filter((_, i) => i !== index);
      setSelectedUserAssignments(updateSelectedUserAssignments);
    }
  };

  /**
   * This function is used to change the user and assignment role dropdowns value
   * @param index - Index value of user assignment row array
   * @param field - Field name
   * @param value - Dropdown selection value
   */
  const handleDropdownChange = (index: number, field: string, value: number) => {
    // Check if view mode is EditProduct and user assignment dropdown(s) contains selected value from dropdown options
    if (selectedUserAssignments[index][field] && !isCopyMode) {
      setShowDropdownChangeConfirmationModal(true);
      setNewDropdownValue({
        index: index,
        field: field,
        value: value
      });
    }
    // else change dropdown selection with updating the state value
    else {
      setNewDropdownValue(null);
      const updateSelectedUserAssignments = [...selectedUserAssignments];
      updateSelectedUserAssignments[index][field] = value;
      setSelectedUserAssignments(updateSelectedUserAssignments);
    }
  };

  // This function is used to handle dropdown selection change confirmation modal for Edit Product
  const handleDropdownChangeConfirm = () => {
    const { index, field, value } = newDropdownValue;
    const updateSelectedUserAssignments = [...selectedUserAssignments];
    updateSelectedUserAssignments[index][field] = value;
    setSelectedUserAssignments(updateSelectedUserAssignments);
    handleDropdownChangeModalClose();
  };

  // This function is used to handle delete user assignment row - confirmation modal for Edit Product
  const handleDeleteConfirm = () => {
    const updateSelectedUserAssignments = selectedUserAssignments.filter((_, i) => i !== deleteRowIndex);
    setSelectedUserAssignments(updateSelectedUserAssignments);
    setDeleteRowIndex(null);
    handleDeleteModalClose();
  };

  // This function is used to handle dropdown change confirmation modal close for Edit Product
  const handleDropdownChangeModalClose = () => {
    setShowDropdownChangeConfirmationModal(false);
  };

  // This function is used to handle delete confirmation modal close for Edit Product
  const handleDeleteModalClose = () => {
    setShowDeleteConfirmationModal(false);
  };

  const columns: GridColDef<GridValidRowModel>[] = [
    { field: "id", headerName: "ID", width: 50, disableColumnMenu: true },
    {
      field: "userId",
      headerName: "User",
      width: 500,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (params) => {
        if(selectedUserAssignments?.length > 0) {
          const currentUser = userDropdownOptions?.find(user => user.id === params?.value);
          return currentUser?.name || "-"
        } else {
          return "-"
        }
      }
    },
    {
      field: "assignmentId",
      headerName: "Assignment",
      type: "string",
      width: 500,
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      valueFormatter: (params) => {
        if(selectedUserAssignments?.length > 0) {
          const currentAssignment = assignmentRoleDropdownOptions?.find(assignment => assignment.id === params?.value);
          return currentAssignment?.name || "-"
        } else {
          return "-"
        }
      }
    },
  ]

  const handleOpenAssignmentsPage = () => {
    dispatch(updateProductAssignmentsViewSelectedFilterOptions({
      baseCode
    }));
    dispatch(updateProductAssignmentsViewSelectedFilterValues({
      baseCode
    }));
    dispatch(updateProductAssignmentsListRequest({
      ...initialProductAssignmentsListRequest,
      baseCode
    }));
    window.open("/product-assignment", "_blank", "noopener,noreferrer");
  }

  return (
    <>
      <ConfirmationModal
        isOpen={showDropdownChangeConfirmationModal}
        onClose={handleDropdownChangeModalClose}
        title={"Confirm User Assignment Modification"}
        message={"Are you sure you want to modify this user assignment?"}
        handleConfirmYes={handleDropdownChangeConfirm}
        handleConfirmNo={handleDropdownChangeModalClose}
      />
      <ConfirmationModal
        isOpen={showDeleteConfirmationModal}
        onClose={handleDeleteModalClose}
        title={"Confirm User Assignment Deletion"}
        message={"Are you sure you want to delete this user assignment?"}
        handleConfirmYes={handleDeleteConfirm}
        handleConfirmNo={handleDeleteModalClose}
      />
      <div className={`user-assignments ${classNames}`}>
        <Box className="custom-box"
          sx={{
            ul: {
              listStyle: "none",
              display: "flex",
              flexDirection: "row",
              li: {
                padding: 1,
                border: `solid 1px ${CSSDefaults.lightColor}`,
                margin: 1,
                borderRadius: "2px",
              },
            },
          }}
        >
          {viewMode !== PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] && <Typography variant="h6">Product Assignments</Typography>}
          {viewMode !== PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] && !editable && selectedUserAssignments[0]?.userId && <Typography className='product-assignment-note' variant="subtitle1">
            <span><strong>Note: </strong>These are the existing product assignments for this Basecode. </span>
            <span>To modify, please <Button className='redirect-link' onClick={() => handleOpenAssignmentsPage()}>click here</Button> to manage Product Assignments.</span>
          </Typography>}
          {viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] && 
            <>
              <Typography className='assignment-details basecode' variant="h6"><span className="label">Base Code - </span><span>{baseCode}</span></Typography>
              <Typography className='assignment-details short-desc' variant="h6"><span className="label">Short Description - </span><span>{shortDesc}</span></Typography>
            </>}
          {(viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] || editable) && selectedUserAssignments.map((field, index) => (
            <div key={index + 1} className={`select-${index}`} style={{ display: "flex", alignItems: "center" }}>
              {Object.keys(field).filter((key) => key !== "id").map((fieldKey, i) => {
                const dropdownOptions = ((fieldKey == "userId" ? userDropdownOptions : assignmentRoleDropdownOptions) || []) as { id: number; name: string }[];
                const selectedOptionName = dropdownOptions.find((option) => option.id === selectedUserAssignments[index][fieldKey])?.name;
                return (
                <FormControl key={i + 1}>
                  <Select
                    displayEmpty={true}
                    value={selectedUserAssignments[index][fieldKey] || ""}
                    onChange={(e) => handleDropdownChange(index, fieldKey, e.target.value as number)}
                    disabled={sessionService.hasPermission(Permissions.ManageProductAssignments) ? 
                      (fieldKey == "assignmentId" && !selectedUserAssignments[index]["userId"]): true}
                    error={fieldKey == "assignmentId" && !selectedUserAssignments[index]["assignmentId"]}
                    SelectDisplayProps={{ title: selectedOptionName || undefined }}
                  >
                    <MenuItem value="" disabled>
                      {fieldKey == "userId" ? "- Select User -" : "- Select Assignment -"}
                    </MenuItem>
                    {dropdownOptions.map((option) => {
                        const isAssignmentAlreadyAssigned = selectedUserAssignments.some((ua, innerIndex) => 
                          innerIndex !== index && ua?.userId === selectedUserAssignments[index]["userId"] && ua?.assignmentId === option.id
                        );

                        const isUserAlreadyAssigned = selectedUserAssignments.some((ua, innerIndex) => 
                          innerIndex !== index && ua?.userId === option.id && ua?.assignmentId === selectedUserAssignments[index]["assignmentId"]
                        );

                        return (
                          <MenuItem 
                            key={option.id} 
                            value={option.id}
                            title={option.name}
                            disabled={(fieldKey === "assignmentId" && isAssignmentAlreadyAssigned) || (fieldKey === "userId" && isUserAlreadyAssigned)}
                          >
                            {option.name}
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
                );
              })}
              {(index > 0 && (viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] || editable)) && (
                sessionService.hasPermission(Permissions.ManageProductAssignments)
                 && <ClearIcon sx={{ cursor: "pointer", marginLeft: "5px" }} onClick={() => handleUserAssignmentRemove(index)}></ClearIcon>
              )}
            </div>
          ))}


          {viewMode !== PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] && !editable && selectedUserAssignments[0]?.userId && <Box className="user-assignments-readonly" sx={{ height: "auto", width: "100%" }}>
            <GridForReadonlyData
              rows={selectedUserAssignments}
              columns={columns}
            />
          </Box>}

          {sessionService.hasPermission(Permissions.ManageProductAssignments) && (viewMode === PermissionsFriendlyNames[VIEW_PRODUCT_ASSIGNMENT] || editable)
          && <Button sx={{ marginTop: "5px" }} onClick={handleAddNewRow} disabled={isAddNewRowDisabled}>
            <AddIcon />
            Add New Row
          </Button>}
        </Box>
      </div>
    </>
  );
};

export default UserAssignments;
